import type { ReactNode } from 'react';
import { getEntityTone, getEntityTypeName, type EntityToneKey } from '../lib/entityRegistry';
import { EntityIcon } from '../lib/entityIcons';
import styles from './EntityTypeMark.module.css';

const TONE_CLASS: Record<EntityToneKey, string> = {
  catalog: styles.tone_catalog,
  commerce: styles.tone_commerce,
  quote: styles.tone_quote,
  procure: styles.tone_procure,
  receive: styles.tone_receive,
  partner: styles.tone_partner,
  client: styles.tone_client,
  support: styles.tone_support,
  staff: styles.tone_staff,
  neutral: styles.tone_neutral,
};

export type EntityTypeMarkProps = {
  entityType: string | null | undefined;
  /** Override visible label (default: registry FR name). */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
};

/** Name + glyph + pastel tone — use wherever an entity type is mentioned. */
export function EntityTypeMark({ entityType, label, size = 'sm', className }: EntityTypeMarkProps) {
  const tone = getEntityTone(entityType);
  const name = label ?? getEntityTypeName(entityType);
  const iconSize = size === 'md' ? 14 : 12;

  return (
    <span
      className={[styles.root, size === 'md' ? styles.rootMd : '', TONE_CLASS[tone], className]
        .filter(Boolean)
        .join(' ')}
      data-entity-type={entityType ?? undefined}
      data-entity-tone={tone}
      title={name}
    >
      <span className={styles.glyph} aria-hidden>
        <EntityIcon entityType={entityType} size={iconSize} color="currentColor" />
      </span>
      <span className={styles.label}>{name}</span>
    </span>
  );
}

export type EntityToneWellProps = {
  entityType: string | null | undefined;
  size?: 'sm' | 'md';
  className?: string;
  children?: ReactNode;
  /** Shape only — no pastel well box. */
  bare?: boolean;
};

/** Toned icon well (leading mark when no product image). */
export function EntityToneWell({
  entityType,
  size = 'md',
  className,
  children,
  bare = false,
}: EntityToneWellProps) {
  const tone = getEntityTone(entityType);
  const iconSize = bare ? (size === 'sm' ? 16 : 20) : size === 'sm' ? 15 : 18;
  return (
    <span
      className={[
        bare ? styles.markBare : styles.well,
        !bare && size === 'sm' ? styles.wellSm : '',
        bare && size === 'sm' ? styles.markBareSm : '',
        TONE_CLASS[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-entity-tone={tone}
      aria-hidden
    >
      {children ?? <EntityIcon entityType={entityType} size={iconSize} color="currentColor" />}
    </span>
  );
}
